import React, { useMemo, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import AkCrossIcon from '@atlaskit/icon/glyph/cross';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import { getAdjustedTopPropertyValue } from './utils/getAdjustedTopPropertyValue';
import { BannerContainer, Banner, IconContainer } from './styled';
import type { EditorBannerProps, EditorBannerContainerProps } from './OnboardingEditorBanner';

const i18n = defineMessages({
	bannerMessage: {
		id: 'onboarding-editor.draft.banner',
		defaultMessage:
			'{boldedUnpublishedDraftText} Only you can see it until you publish it or give someone the link. {learnMoreLink}',
	},
	unpublishedDraftText: {
		id: 'onboarding-editor.unpublished.draft',
		defaultMessage: '<b>This is an unpublished draft.</b>',
		description: 'This is an unpublished draft.',
	},
	learnMore: {
		id: 'onboarding-editor.learn.more',
		defaultMessage: 'Learn more',
		description: 'Learn more about drafts',
	},
});

export const EditorDraftBanner = (props: EditorBannerProps & EditorBannerContainerProps) => {
	const {
		isContentCreationPanelOpen,
		localUserOffline,
		setIsVisible,
		deserializedData: { wasDraftBannerSeen } = {},
		setIsOnboardingBannerDataLoaded,
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useOnboardingState();

	useEffect(() => {
		if (!wasDraftBannerSeen) {
			void setOnboardingState([
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.WAS_DRAFT_BANNER_SEEN,
					value: 'true',
				},
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.IS_DRAFT_BANNER_VISIBLE,
					value: 'true',
				},
			]);

			const editorNode: HTMLElement | null = document.querySelector('.ak-editor-content-area');

			if (editorNode) {
				editorNode.style.marginTop = '52px';
			}

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'onboardingEditorDraft',
				},
			}).fire();
			// TODO: Refactor https://hello.atlassian.net/browse/CONFONBO-1674
			setTimeout(() => {
				setIsOnboardingBannerDataLoaded(true);
			}, 0);
		}
	}, [
		createAnalyticsEvent,
		setIsOnboardingBannerDataLoaded,
		setOnboardingState,
		wasDraftBannerSeen,
	]);

	const _onDismiss = () => {
		setIsVisible(false);
		// Move all the content back up 52px to adjust for the banner disappearing
		const editorNode: HTMLElement | null = document.querySelector('.ak-editor-content-area');

		if (editorNode) {
			editorNode.style.marginTop = '0px';
		}
	};

	// compiled shouldn't handle a complicated runtime CSS, hence the inlined style
	const calculatedTop = useMemo(
		() => getAdjustedTopPropertyValue(localUserOffline),
		[localUserOffline],
	);

	return (
		<BannerContainer
			style={{ top: calculatedTop }}
			isContentCreationPanelOpen={isContentCreationPanelOpen}
			data-testid="onboarding-editor-draft-banner"
		>
			<Banner>
				<FormattedMessage
					{...i18n.bannerMessage}
					values={{
						boldedUnpublishedDraftText: (
							<FormattedMessage
								{...i18n.unpublishedDraftText}
								values={{ b: (chunks) => <b>{chunks}</b> }}
							/>
						),
						learnMoreLink: (
							<a
								href="https://support.atlassian.com/confluence-cloud/docs/add-or-remove-page-restrictions/"
								target="_blank"
							>
								<FormattedMessage {...i18n.learnMore} />
							</a>
						),
					}}
				/>
				<IconContainer data-testid="onboarding-editor-close-banner" onClick={_onDismiss}>
					<AkCrossIcon label="dismiss" size="small" />
				</IconContainer>
			</Banner>
		</BannerContainer>
	);
};
